import { Model } from '@mode-vegas/vegas';
import { assign, find, some } from 'lodash';

import { Injectable } from '@angular/core';
import { ReportDatasetRecord } from '@mode/legacy/types-app-state';
import { straight as straightData } from '../lib/axel/model';
import { naturalSort, nullToBottomSort, numericalSort } from '../lib/axel/sorter';
import { AxelDate, AxelNumber, AxelString } from '../lib/axel/type';
import { orderedComparatorMixin } from '../lib/axel/util';

interface ModelResult extends ReportDatasetRecord {
  model: Model;
}

export interface DatasetsMap {
  [queryToken: string]: any;
}

@Injectable({ providedIn: 'root' })
class DatasetService {
  public dataset = null;
  public datasets: DatasetsMap = {};
  public models: { [queryToken: string]: ModelResult } = {};
  public datasets_raw: any[] = null;
  public dismissed = {};
  public queryRuns?: any;

  constructor() {}

  loadDataset(data: any) {
    this.dataset = data;
  }

  loadKeyedDatasets(sets: any) {
    this.datasets = sets;
  }

  loadRawDatasets(sets: any) {
    this.datasets_raw = sets;
  }

  addRawDataset(set: any) {
    if (this.datasets_raw.indexOf(set) < 0) {
      this.datasets_raw.push(set);
    }
  }

  getDataset() {
    return this.dataset;
  }

  getKeyedDatasets() {
    return this.datasets || {};
  }

  getRawDatasets() {
    return this.datasets_raw;
  }

  getDatasetByKey(token) {
    return this.getKeyedDatasets()[token];
  }

  getDatasetModel(token, opts = {}, dataset = null) {
    if (!token && !dataset) {
      throw new Error('No token or dataset provided');
    }
    dataset = dataset || this.getDatasetByKey(token);
    const colTypeFn = this.resolveColumnDataType.bind(this, token);
    const datasetContent = this.getStaticDatasetContent(dataset);

    return straightData(
      datasetContent,
      assign(
        {},
        {
          value: (data, colKey) => () => this.getValue.call(this, data, this.resolveColumnDataType(token, colKey)),
          columns: this.getStaticDatasetColumnMetaData(dataset).map((col) => col.name),
          sorter: this.sortTable.bind(this, colTypeFn),
        },
        opts
      )
    );
  }

  resolveColumnDataType(queryToken, colKey) {
    const DEFAULT_VALUE_TYPE = 'plain-text';
    const dataTypeMap = {
      number: 'number',
      integer: 'number',
      float: 'number',
      real: 'number',
      date: 'date',
      datetime: 'date',
    };
    const resolveSelectedTypeFromDataType = (colKey) => {
      const dataType = this.columnDataType(queryToken, colKey);
      return dataTypeMap[dataType] || DEFAULT_VALUE_TYPE;
    };

    return resolveSelectedTypeFromDataType(colKey);
  }

  getStaticDatasetContent(dataset) {
    return dataset.content;
  }

  getStaticDatasetColumnMetaData(dataset) {
    return dataset.columns;
  }

  getDatasetContent(token) {
    if (!this.hasContentFor(token)) {
      throw new Error('Dataset not available for given token');
    }
    return this.getDatasetByKey(token).content;
  }

  hasContentFor(token) {
    const dataset = this.getKeyedDatasets()[token];
    return !!(dataset && dataset.content && dataset.state === 'succeeded');
  }

  hasFailed(token) {
    const dataset = this.getDatasetByKey(token);
    const resource = this.getDatasetResource(token);
    return !!((resource && resource.state === 'failed') || (dataset && dataset.state === 'failed'));
  }

  /**
   * Get Value
   * @param data
   * @param colType
   * @returns {any}
   */
  getValue(data, colType) {
    const typeCasters = {
      'plain-text': (d) => new AxelString(d),
      date: (d) => new AxelDate(d),
      financial: (d) => new AxelNumber(d),
      currency: (d) => new AxelNumber(d),
      number: (d) => new AxelNumber(d),
      percentage: (d) => new AxelNumber(d),
    };

    return typeCasters[colType](data);
  }

  /**
   * Sort Table
   * @param colTypeFn
   * @param colKey
   * @param order
   * @param aSortable
   * @param bSortable
   * @returns {any}
   */
  sortTable(colTypeFn, colKey, order, aSortable, bSortable) {
    const a = aSortable.data;
    const b = bSortable.data;
    const epoch = (date) => (date.getTime ? date.getTime() : null);
    const columnType = colTypeFn(colKey);
    // curry mixin helper with nullToBottomSort comparator
    const nullSortMixin = (comparator) => orderedComparatorMixin(nullToBottomSort, comparator, order);
    const sorters = {
      'plain-text': () => nullSortMixin(naturalSort)(a.toStringOrNull(), b.toStringOrNull()),
      date: () => nullSortMixin(numericalSort)(epoch(a.valueOf()), epoch(b.valueOf())),
      number: () => nullSortMixin(numericalSort)(a.valueOrNull(), b.valueOrNull()),
      percentage: () => sorters.number(),
      financial: () => sorters.number(),
      currency: () => sorters.number(),
    };

    return (sorters[columnType] || sorters['plain-text'])();
  }

  getDatasetResource(token) {
    return this.getDatasetByKey(token) && this.getDatasetByKey(token)['_resource'];
  }

  getDatasetState(token) {
    const dataset = this.getDatasetByKey(token);

    if (!dataset) {
      return '';
    }
    if (dataset['state']) {
      return this.getDatasetByKey(token)['state'];
    } else {
      return this.getDatasetResource(token)['state'];
    }
  }

  /**
   * Column Data Type
   *
   * @todo rethink the error handling here
   * @param queryToken
   * @param colKey
   * @returns {null}
   */
  public columnDataType(queryToken, colKey) {
    if (!this.hasContentFor(queryToken)) {
      return null;
    }

    const queryData = this.getDatasetByKey(queryToken);
    const targetColumn: any = find(queryData.columns, { name: colKey });

    return targetColumn ? targetColumn.type : null;
  }

  datasetIsTooLarge(queryToken: string): boolean {
    return !this.hasContentFor(queryToken);
  }

  datasetsAreExportable(datasets: DatasetsMap = this.datasets, queryRuns: any = this.queryRuns): boolean {
    return some(datasets, function hasData(dataset) {
      const success =
        queryRuns && queryRuns[dataset.query_token] && queryRuns[dataset.query_token].state === 'succeeded';
      const notEmpty = dataset.count > 0 || dataset.oversized;

      return success && notEmpty;
    });
  }
}

export default DatasetService;
